// channelNodeMentions.js

import { getActiveTriggerQuery } from 'channels/utils/channelMentions';

const normalizeKey = (value) => String(value || '').trim();

const EXCLUDED_NODE_MENTION_TYPES = new Set([
  'USER',
  'LIVE_ROOM',
  'LIVE_STREAM',
]);

/**
 * Returns active #-node-mention query details when the cursor is inside a token.
 *
 * @param {string} text - Composer text.
 * @param {number} cursorPosition - Textarea selection start.
 * @returns {{ query: string, start: number, end: number }|null}
 */
export function getActiveNodeMentionQuery(text, cursorPosition) {
  return getActiveTriggerQuery(text, cursorPosition, '#');
}

/**
 * Whether a node type should be offered in #-mention autocomplete.
 *
 * @param {string} typeKey - Node type key.
 * @returns {boolean}
 */
export function isNodeMentionTypeAllowed(typeKey) {
  const normalized = String(typeKey || '').trim().toUpperCase();
  if (!normalized) return true;
  return !EXCLUDED_NODE_MENTION_TYPES.has(normalized);
}

/**
 * Maps search rows into #-mention autocomplete candidates.
 *
 * @param {Array} rows - Base node search rows.
 * @param {function} resolveTypeLabel - (typeKey) => display label.
 * @returns {Array<{ nodeKey: string, title: string, nodeType: string, typeLabel: string }>}
 */
export function buildNodeMentionCandidates(rows = [], resolveTypeLabel) {
  const seen = new Set();

  return (Array.isArray(rows) ? rows : [])
    .map((row) => {
      const nodeKey = normalizeKey(row?.key || row?.id || row?.nodeKey);
      const title = String(row?.title || '').trim();
      const nodeType = String(row?.type || row?.nodeType || 'NODE').trim() || 'NODE';

      if (!nodeKey || !title || seen.has(nodeKey)) return null;
      if (!isNodeMentionTypeAllowed(nodeType)) return null;

      seen.add(nodeKey);

      const typeLabel = typeof resolveTypeLabel === 'function'
        ? resolveTypeLabel(nodeType)
        : nodeType;

      return {
        nodeKey,
        title,
        nodeType,
        typeLabel: String(typeLabel || nodeType).trim() || nodeType,
      };
    })
    .filter(Boolean)
    .slice(0, 8);
}

/**
 * Inserts a selected node mention into composer text.
 *
 * @param {string} text - Current composer text.
 * @param {number} start - Token start index (at #).
 * @param {number} end - Token end index (cursor).
 * @param {{ nodeKey: string, title: string, nodeType?: string }} selection
 * @returns {{ text: string, cursor: number, token: { nodeKey: string, title: string, nodeType: string } }}
 */
export function insertNodeMentionToken(text, start, end, {
  nodeKey,
  title,
  nodeType = 'NODE',
} = {}) {
  const cleanTitle = String(title || '').trim();
  const cleanKey = normalizeKey(nodeKey);
  const cleanType = String(nodeType || 'NODE').trim() || 'NODE';
  const prefix = String(text || '').slice(0, start);
  const suffix = String(text || '').slice(end);
  const mentionText = `#${cleanTitle} `;
  const nextText = `${prefix}${mentionText}${suffix}`;

  return {
    text: nextText,
    cursor: prefix.length + mentionText.length,
    token: {
      nodeKey: cleanKey,
      title: cleanTitle,
      nodeType: cleanType,
    },
  };
}

/**
 * Keeps node mention tokens that still appear in the composer text.
 *
 * @param {string} text - Composer text.
 * @param {Array<{ nodeKey: string, title: string, nodeType?: string }>} tokens
 * @returns {Array<{ nodeKey: string, title: string, nodeType: string }>}
 */
export function syncNodeMentionTokens(text, tokens = []) {
  const value = String(text || '');

  return (Array.isArray(tokens) ? tokens : [])
    .filter((token) => {
      const title = String(token?.title || '').trim();
      const nodeKey = normalizeKey(token?.nodeKey);
      if (!title || !nodeKey) return false;
      return value.includes(`#${title}`);
    })
    .reduce((acc, token) => {
      const nodeKey = normalizeKey(token.nodeKey);
      if (!nodeKey || acc.some((row) => row.nodeKey === nodeKey)) return acc;
      acc.push({
        nodeKey,
        title: String(token.title || '').trim(),
        nodeType: String(token.nodeType || 'NODE').trim() || 'NODE',
      });
      return acc;
    }, []);
}

/**
 * Converts tracked node mention tokens into attachment reference rows.
 *
 * @param {Array<{ nodeKey: string, title: string, nodeType?: string }>} tokens
 * @returns {Array<{ nodeKey: string, title: string, nodeType: string }>}
 */
export function resolveNodeReferencesFromMentionTokens(tokens = []) {
  return (Array.isArray(tokens) ? tokens : [])
    .map((token) => {
      const nodeKey = normalizeKey(token?.nodeKey);
      const title = String(token?.title || '').trim();
      if (!nodeKey || !title) return null;

      return {
        nodeKey,
        title,
        nodeType: String(token?.nodeType || 'NODE').trim() || 'NODE',
      };
    })
    .filter(Boolean);
}

/**
 * Merges node reference rows by node key without duplicating entries.
 *
 * @param {Array} primaryRows - Existing / preferred rows.
 * @param {Array} secondaryRows - Rows to merge in.
 * @returns {Array<{ nodeKey: string, title: string, nodeType: string }>}
 */
export function mergeNodeReferences(primaryRows = [], secondaryRows = []) {
  const nextByNodeKey = new Map();

  const append = (rows) => {
    (Array.isArray(rows) ? rows : []).forEach((row) => {
      const nodeKey = normalizeKey(row?.nodeKey || row?.key);
      if (!nodeKey) return;

      const existing = nextByNodeKey.get(nodeKey);
      nextByNodeKey.set(nodeKey, {
        nodeKey,
        title: String(row?.title || existing?.title || '').trim(),
        nodeType: String(
          row?.nodeType || row?.type || existing?.nodeType || 'NODE'
        ).trim() || 'NODE',
      });
    });
  };

  append(primaryRows);
  append(secondaryRows);

  return Array.from(nextByNodeKey.values());
}

/**
 * Removes the first `#Title` occurrence for a node mention from composer text.
 *
 * @param {string} text - Composer text.
 * @param {string} title - Node title previously inserted after #.
 * @returns {string}
 */
export function stripNodeMentionFromText(text, title) {
  const cleanTitle = String(title || '').trim();
  if (!cleanTitle) return String(text || '');

  const needle = `#${cleanTitle}`;
  const value = String(text || '');
  const index = value.indexOf(needle);
  if (index < 0) return value;

  const before = value.slice(0, index);
  let after = value.slice(index + needle.length);
  if (after.startsWith(' ')) after = after.slice(1);

  const joined = `${before}${after}`;
  return joined.replace(/  +/g, ' ').trimStart();
}

/**
 * Builds titles for #-mention highlighting from node reference attachments.
 *
 * @param {Array} attachments - Message attachments.
 * @returns {string[]}
 */
export function buildNodeMentionDisplayTitles(attachments = []) {
  return Array.from(new Set(
    (Array.isArray(attachments) ? attachments : [])
      .filter((attachment) => String(attachment?.type || '').trim() === 'node_ref')
      .map((attachment) => String(attachment?.title || '').trim())
      .filter(Boolean)
  ));
}
